import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  PointerSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useHistory, useLocation } from 'react-router-dom';

import {
  DragIndicator as DragIndicatorIcon,
  ExpandMore as ExpandMoreIcon,
  TableRows as FlatViewIcon,
  AccountTree as GroupedViewIcon,
  InfoOutlined as InfoOutlinedIcon,
  RefreshRounded as RefreshRoundedIcon,
  SearchOffRounded as SearchOffRoundedIcon,
  Visibility as ViewIcon,
  WarningAmberRounded as WarningAmberRoundedIcon,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  Select,
  type SelectChangeEvent,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';

import {
  useGetAttractionCities,
  useGetAttractionFilterOptions,
  useGetAttractionStats,
  useListAttractions,
  useSaveAttractionRankings,
  useUpdateAttractionActive,
} from '~/hooks/apis/attractions';

import {
  type Attraction,
  type AttractionType,
  type GetAttractionStatsParams,
  type ListAttractionsParams,
  type RankChange,
  type RankScopeType,
  formatAttractionType,
} from '~/types/attractionTypes';

import AttractionsDashboard from './AttractionsDashboard';
import { computeSlotDrop, detectActiveScope } from './attractionRankUtils';
import { coverageColor } from './attractionUtils';
import AttractionPerformancePage from './performance/AttractionPerformancePage';

const DEFAULT_PAGE_SIZE = 10;

const SORT_OPTIONS = [
  { value: 'popularity', label: 'Popularity' },
  { value: 'most_offers', label: 'Most Offers' },
  { value: 'state_rank', label: 'State Rank' },
  { value: 'province_rank', label: 'Province Rank' },
  { value: 'city_rank', label: 'City Rank' },
  { value: 'name', label: 'Name' },
] as const;

const TYPE_COLORS: Record<AttractionType, { bg: string; text: string }> = {
  cultural: { bg: '#7C3AED', text: '#fff' },
  natural: { bg: '#059669', text: '#fff' },
  entertainment: { bg: '#DB2777', text: '#fff' },
  religious: { bg: '#D97706', text: '#fff' },
  historical: { bg: '#1A56DB', text: '#fff' },
};

const TABLE_COLUMNS = [
  '',
  '',
  'Image',
  'Name',
  'Location',
  'Type',
  'Offers',
  'Popularity Rank',
  'State Rank',
  'Province Rank',
  'City Rank',
  'Active',
];

const RANK_LIMIT = 5;

/** Stable digest of list rows so we skip snapshot state updates when React Query refetches identical data. */
function attractionRowSnapshotFingerprint(attractions: Attraction[], rankedAttractions: Attraction[]): string {
  const byId = new Map<string, string>();
  const push = (a: Attraction) => {
    byId.set(
      a.attractionId,
      [
        a.name,
        a.city,
        a.state ?? '',
        a.province ?? '',
        a.country,
        a.type,
        a.offerCount,
        a.manualRank ?? '',
        String(a.active),
        a.imageUrl ?? '',
        a.popularityRank,
        a.stateRank ?? '',
        a.provinceRank ?? '',
        a.cityRank ?? '',
      ].join('\x1f'),
    );
  };
  for (const a of attractions) push(a);
  for (const a of rankedAttractions) push(a);
  return [...byId.entries()]
    .sort(([idA], [idB]) => idA.localeCompare(idB))
    .map(([id, sig]) => `${id}=${sig}`)
    .join('|');
}

function pluralize(count: number, singular: string, plural: string): string {
  return count === 1 ? `${count} ${singular}` : `${count} ${plural}`;
}

const IMG_PLACEHOLDER = (
  <Box
    sx={{
      width: 56,
      height: 42,
      borderRadius: 1,
      bgcolor: 'grey.100',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.625rem' }}>
      No img
    </Typography>
  </Box>
);

function ImageWithFallback({ src, size = 'default' }: { src?: string; size?: 'default' | 'small' }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) return IMG_PLACEHOLDER;

  const dims = size === 'small' ? { width: 36, height: 27 } : { width: 56, height: 42 };

  return (
    <Box
      component="img"
      src={src}
      alt=""
      onError={() => setFailed(true)}
      sx={{
        ...dims,
        objectFit: src.includes('wikimedia') ? 'contain' : 'cover',
        backgroundColor: src.includes('wikimedia') ? '#f5f5f5' : undefined,
        borderRadius: 1,
      }}
    />
  );
}

function DraggableHandle({ attractionId }: { attractionId: string }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: `main-${attractionId}` });
  return (
    <Box
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      sx={{ cursor: 'grab', opacity: isDragging ? 0.4 : 0.6, display: 'flex', justifyContent: 'center' }}
    >
      <DragIndicatorIcon fontSize="small" color="action" />
    </Box>
  );
}

function DroppableSlot({ rank, attraction }: { rank: number; attraction: Attraction | null }) {
  const {
    attributes,
    listeners,
    setNodeRef: setDragRef,
    isDragging,
  } = useDraggable({
    id: attraction ? attraction.attractionId : `empty-${rank}`,
    disabled: !attraction,
  });
  const { setNodeRef: setDropRef, isOver: isSlotOver } = useDroppable({
    id: `slot-${rank}`,
  });

  const mergedRef = useCallback(
    (node: HTMLElement | null) => {
      setDragRef(node);
      setDropRef(node);
    },
    [setDragRef, setDropRef],
  );

  return (
    <Box
      ref={mergedRef}
      sx={{
        display: 'flex',
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: isSlotOver ? 'primary.main' : 'divider',
        borderWidth: isSlotOver ? 2 : 1,
        minHeight: 44,
        opacity: isDragging ? 0.4 : 1,
        bgcolor: isSlotOver ? 'action.selected' : undefined,
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Box
        sx={{ width: 32, px: 0.5, display: 'flex', justifyContent: 'center', cursor: attraction ? 'grab' : 'default' }}
        {...(attraction ? attributes : {})}
        {...(attraction && listeners ? listeners : {})}
      >
        {attraction ? <DragIndicatorIcon fontSize="small" color="action" /> : null}
      </Box>
      <Box
        sx={{ width: 48, px: 1, color: 'text.secondary', fontWeight: 600, fontSize: '0.8125rem', textAlign: 'center' }}
      >
        {rank}
      </Box>
      <Box sx={{ width: 72, px: 0.5 }}>
        {attraction && (
          <ImageWithFallback
            src={
              attraction.imageUrl?.startsWith('http')
                ? attraction.imageUrl
                : attraction.imageUrl
                ? `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_60,h_60/${attraction.imageUrl}`
                : undefined
            }
            size="small"
          />
        )}
      </Box>
      <Box sx={{ flex: 2, px: 1, minWidth: 0 }}>
        {attraction ? (
          <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>{attraction.name}</Typography>
        ) : (
          <Typography sx={{ fontSize: '0.8125rem', color: 'text.disabled', fontStyle: 'italic' }}>
            {isSlotOver ? 'Drop here' : '--'}
          </Typography>
        )}
      </Box>
      <Box sx={{ flex: 1, px: 1, minWidth: 0 }}>
        {attraction && (
          <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            {[attraction.countryCode, attraction.state, attraction.province, attraction.city]
              .filter(Boolean)
              .join(' / ')}
          </Typography>
        )}
      </Box>
      <Box sx={{ width: 120, px: 1 }}>
        {attraction && (
          <Chip
            label={formatAttractionType(attraction.type)}
            size="small"
            sx={{
              bgcolor: TYPE_COLORS[attraction.type]?.bg ?? 'grey.500',
              color: TYPE_COLORS[attraction.type]?.text ?? '#fff',
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
        )}
      </Box>
      <Box sx={{ width: 56, px: 1, textAlign: 'center', fontSize: '0.8125rem' }}>
        {attraction ? attraction.offerCount : null}
      </Box>
    </Box>
  );
}

interface RankedAttractionsSummaryProps {
  activeScope: { scopeType: RankScopeType; scopeValue: string };
  rankLimit: number;
  rankedAttractions: Attraction[];
  pageAttractions: Attraction[];
  attractionSnapshotById: Map<string, Attraction>;
  pendingRanks: Map<string, number | null>;
  isFetching: boolean;
}

const SUMMARY_COLUMNS = ['', 'Rank', 'Image', 'Name', 'Location', 'Type', 'Offers'];
const COLUMN_WIDTHS: Array<number | 'flex2' | 'flex1'> = [32, 48, 72, 'flex2', 'flex1', 120, 56];

function RankedAttractionsSummary({
  activeScope,
  rankLimit,
  rankedAttractions,
  pageAttractions,
  attractionSnapshotById,
  pendingRanks,
  isFetching,
}: RankedAttractionsSummaryProps) {
  const theme = useTheme();

  const rankedSlots = useMemo(() => {
    const effectiveRankMap = new Map<string, number>();

    for (const attraction of rankedAttractions) {
      if (attraction.manualRank != null) {
        effectiveRankMap.set(attraction.attractionId, attraction.manualRank);
      }
    }

    for (const [attractionId, rank] of pendingRanks.entries()) {
      if (rank == null) {
        effectiveRankMap.delete(attractionId);
      } else {
        effectiveRankMap.set(attractionId, rank);
      }
    }

    const rankToAttraction = new Map<number, Attraction>();
    for (const [attractionId, rank] of effectiveRankMap.entries()) {
      if (rank >= 1 && rank <= rankLimit) {
        const found =
          pageAttractions.find((a) => a.attractionId === attractionId) ??
          rankedAttractions.find((a) => a.attractionId === attractionId) ??
          attractionSnapshotById.get(attractionId);
        if (found) {
          rankToAttraction.set(rank, found);
        }
      }
    }

    return Array.from({ length: rankLimit }, (_, i) => {
      const rank = i + 1;
      return { rank, attraction: rankToAttraction.get(rank) ?? null };
    });
  }, [rankedAttractions, pageAttractions, attractionSnapshotById, pendingRanks, rankLimit]);

  const hasAnyRank = !isFetching && rankedSlots.some((slot) => slot.attraction != null);

  return (
    <Paper variant="outlined" sx={{ mt: 1, mb: 1 }}>
      <Box sx={{ px: 2, py: 1, borderBottom: hasAnyRank ? '1px solid' : 'none', borderColor: 'divider' }}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.6875rem',
          }}
        >
          Manual Ranking Preview -- {activeScope.scopeType.charAt(0).toUpperCase() + activeScope.scopeType.slice(1)}:{' '}
          {activeScope.scopeValue}
        </Typography>
      </Box>

      {!hasAnyRank && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic' }}>
            Drag attractions from the table below to assign rankings
          </Typography>
        </Box>
      )}
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: theme.palette.primary.dark }}>
          {SUMMARY_COLUMNS.map((col, i) => (
            <Box
              key={`${col}-${i}`}
              sx={{
                px: 1,
                py: 0.75,
                fontSize: 12,
                fontWeight: 600,
                color: 'white',
                whiteSpace: 'nowrap',
                width: typeof COLUMN_WIDTHS[i] === 'number' ? COLUMN_WIDTHS[i] : undefined,
                flex: COLUMN_WIDTHS[i] === 'flex2' ? 2 : COLUMN_WIDTHS[i] === 'flex1' ? 1 : undefined,
                minWidth: typeof COLUMN_WIDTHS[i] === 'string' ? 0 : undefined,
              }}
            >
              {col}
            </Box>
          ))}
        </Box>
        {rankedSlots.map(({ rank, attraction }) => (
          <DroppableSlot key={rank} rank={rank} attraction={attraction} />
        ))}
      </Box>
    </Paper>
  );
}

function RankCell({
  row,
  scopeType,
  activeScope,
  pendingRanks,
  rankLimit,
  usedRanks,
  onRankChange,
  fallback,
}: {
  row: Attraction;
  scopeType: RankScopeType;
  activeScope: { scopeType: RankScopeType; scopeValue: string } | null;
  pendingRanks: Map<string, number | null>;
  rankLimit: number;
  usedRanks: Map<number, string>;
  onRankChange: (attractionId: string, value: string) => void;
  fallback: React.ReactNode;
}) {
  if (activeScope?.scopeType !== scopeType) {
    return <>{fallback}</>;
  }

  const currentValue: number | '' = pendingRanks.has(row.attractionId)
    ? pendingRanks.get(row.attractionId) ?? ''
    : row.manualRank ?? '';

  return (
    <Select<number | ''>
      size="small"
      value={currentValue}
      displayEmpty
      disabled={!row.active}
      onChange={(e) => onRankChange(row.attractionId, String(e.target.value))}
      sx={{
        minWidth: 56,
        '& .MuiSelect-select': { py: 0.5, fontSize: '0.8125rem' },
        ...(pendingRanks.has(row.attractionId) && {
          '& .MuiOutlinedInput-notchedOutline': { borderColor: 'warning.main', borderWidth: 2 },
        }),
      }}
    >
      <MenuItem value="">
        <Typography variant="body2" color="text.disabled">
          -
        </Typography>
      </MenuItem>
      {Array.from({ length: rankLimit }, (_, i) => i + 1).map((n) => {
        const usedBy = usedRanks.get(n);
        const isUsedByOther = usedBy != null && usedBy !== row.attractionId;
        return (
          <MenuItem key={n} value={n}>
            {n}
            {isUsedByOther && (
              <Typography component="span" variant="caption" color="text.disabled" sx={{ ml: 0.5 }}>
                (swap)
              </Typography>
            )}
          </MenuItem>
        );
      })}
    </Select>
  );
}

export default function AttractionsPage() {
  const theme = useTheme();
  const themeColor = theme.palette.primary.main;
  const history = useHistory();
  const location = useLocation();

  const activeMutation = useUpdateAttractionActive();
  const rankMutation = useSaveAttractionRankings();
  const [pendingRanks, setPendingRanks] = useState<Map<string, number | null>>(new Map());
  const [attractionRowSnapshotById, setAttractionRowSnapshotById] = useState(() => new Map<string, Attraction>());
  const [activeDragId, setActiveDragId] = useState<string | null>(null);

  const subtab = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const val = sp.get('subtab');
    return val === 'performance' ? 'performance' : 'curation';
  }, [location.search]);

  const handleSubtabChange = useCallback(
    (_: React.SyntheticEvent, value: string) => {
      const sp = new URLSearchParams(location.search);
      sp.set('subtab', value);
      sp.set('tab', 'attractions');
      history.replace({ search: sp.toString() });
    },
    [history, location.search],
  );

  const initialParams = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      country: sp.get('country') || 'Australia',
      state: sp.get('state') || undefined,
      province: sp.get('province') || undefined,
      city: sp.get('city') || undefined,
      sort: sp.get('sort') || 'popularity',
      page: Math.max(1, Number(sp.get('page')) || 1),
      pageSize: Number(sp.get('pageSize')) || DEFAULT_PAGE_SIZE,
      viewMode: (sp.get('view') === 'grouped' ? 'grouped' : 'flat') as 'flat' | 'grouped',
      activeOnly: sp.get('activeOnly') !== 'false',
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [page, setPage] = useState(initialParams.page);
  const [pageSize, setPageSize] = useState(initialParams.pageSize);
  const [drillCountryCodeOverride, setDrillCountryCodeOverride] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState(initialParams.activeOnly);

  const [searchText, setSearchText] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const searchTimer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleSearchChange = useCallback((value: string) => {
    setSearchText(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {
      setSearchDebounced(value);
      setPage(1);
    }, 400);
  }, []);

  useEffect(() => {
    return () => clearTimeout(searchTimer.current);
  }, []);

  const [filters, setFilters] = useState<ListAttractionsParams>({
    sort: initialParams.sort,
    country: initialParams.country,
    state: initialParams.state,
    province: initialParams.province,
    city: initialParams.city,
    limit: initialParams.pageSize,
    offset: (initialParams.page - 1) * initialParams.pageSize,
  });

  const [viewMode, setViewMode] = useState<'flat' | 'grouped'>(initialParams.viewMode);

  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const sp = new URLSearchParams();
    sp.set('tab', 'attractions');
    if (subtab !== 'curation') sp.set('subtab', subtab);
    if (filters.country) sp.set('country', filters.country);
    if (filters.state) sp.set('state', filters.state);
    if (filters.province) sp.set('province', filters.province);
    if (filters.city) sp.set('city', filters.city);
    if (filters.sort && filters.sort !== 'popularity') sp.set('sort', filters.sort);
    if (page > 1) sp.set('page', String(page));
    if (pageSize !== DEFAULT_PAGE_SIZE) sp.set('pageSize', String(pageSize));
    if (viewMode !== 'flat') sp.set('view', viewMode);
    if (!activeOnly) sp.set('activeOnly', 'false');
    history.replace({ search: sp.toString() });
  }, [filters, page, pageSize, viewMode, activeOnly, subtab, history]);

  const { data: citiesData } = useGetAttractionCities();
  const cities = citiesData?.result ?? [];

  const countries = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of cities) {
      if (c.country && c.countryCode) map.set(c.country, c.countryCode);
    }
    return Array.from(map.entries())
      .map(([country, countryCode]) => ({ country, countryCode }))
      .sort((a, b) => a.country.localeCompare(b.country));
  }, [cities]);

  const selectedCountryCode = useMemo(() => {
    if (!filters.country) return undefined;
    return countries.find((c) => c.country === filters.country)?.countryCode;
  }, [filters.country, countries]);

  const { data: filterOptionsData } = useGetAttractionFilterOptions({
    countryCode: selectedCountryCode,
    state: filters.state,
    province: filters.province,
  });
  const filterOptions = filterOptionsData?.result;

  const drillCountryCode = useMemo(() => {
    if (filters.country) {
      const match = countries.find((c) => c.country === filters.country);
      return match?.countryCode ?? null;
    }
    return drillCountryCodeOverride;
  }, [filters.country, countries, drillCountryCodeOverride]);

  const setDrillCountryCode = useCallback(
    (code: string | null) => {
      setDrillCountryCodeOverride(code);
      if (code) {
        const match = countries.find((c) => c.countryCode === code);
        if (match) {
          setFilters((prev) => ({
            ...prev,
            country: match.country,
            state: undefined,
            province: undefined,
            city: undefined,
            offset: 0,
          }));
          setPage(1);
        }
      } else {
        setFilters((prev) => ({
          ...prev,
          country: undefined,
          state: undefined,
          province: undefined,
          city: undefined,
          offset: 0,
        }));
        setPage(1);
      }
    },
    [countries],
  );

  const filteredCities = useMemo(
    () => (filters.country ? cities.filter((c) => c.country === filters.country) : cities),
    [cities, filters.country],
  );

  const statsParams: GetAttractionStatsParams = useMemo(() => {
    const params: GetAttractionStatsParams = {};
    if (filters.country) {
      const match = countries.find((countryOption) => countryOption.country === filters.country);
      if (match) params.countryCode = match.countryCode;
    }
    if (filters.countryCode) params.countryCode = filters.countryCode;
    if (filters.city) params.city = filters.city;
    return params;
  }, [filters, countries]);

  const hasActiveFilters =
    Object.values(statsParams).some(Boolean) ||
    !!searchDebounced ||
    activeOnly ||
    !!filters.state ||
    !!filters.province;

  const {
    data: globalStatsData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    refetch: refetchDashboard,
  } = useGetAttractionStats({}, { enabled: subtab === 'curation' });
  const { data: globalGroupedData } = useGetAttractionStats(
    { groupBy: 'city' as const },
    { enabled: subtab === 'curation' },
  );
  const globalStats = globalStatsData?.result ?? null;

  const dashboardStats = useMemo(() => {
    if (!globalStats) return null;
    if (!globalGroupedData?.result) return globalStats;
    const cityMap = new Map<string, (typeof globalGroupedData.result.countries)[0]>();
    for (const c of globalGroupedData.result.countries) {
      cityMap.set(c.countryCode, c);
    }
    return {
      ...globalStats,
      countries: globalStats.countries.map((c) => ({
        ...c,
        cities: cityMap.get(c.countryCode)?.cities ?? [],
      })),
    };
  }, [globalStats, globalGroupedData]);

  const groupedStatsParams: GetAttractionStatsParams = useMemo(
    () => ({ ...statsParams, groupBy: 'city' as const }),
    [statsParams],
  );
  const { data: groupedData, isLoading: isGroupedLoading } = useGetAttractionStats(groupedStatsParams, {
    enabled: subtab === 'curation',
  });
  const groupedStats = groupedData?.result ?? null;

  const activeFilterChips = useMemo(() => {
    const chips: Array<{ key: string; label: string; onDelete: () => void }> = [];
    if (filters.country) {
      chips.push({
        key: 'country',
        label: `Country: ${filters.country}`,
        onDelete: () => {
          setFilters((prev) => ({
            ...prev,
            country: undefined,
            countryCode: undefined,
            state: undefined,
            province: undefined,
            city: undefined,
            offset: 0,
          }));
          setPage(1);
        },
      });
    }
    if (filters.state) {
      chips.push({
        key: 'state',
        label: `State: ${filters.state}`,
        onDelete: () => {
          setFilters((prev) => ({ ...prev, state: undefined, province: undefined, city: undefined, offset: 0 }));
          setPage(1);
        },
      });
    }
    if (filters.province) {
      chips.push({
        key: 'province',
        label: `Province: ${filters.province}`,
        onDelete: () => {
          setFilters((prev) => ({ ...prev, province: undefined, city: undefined, offset: 0 }));
          setPage(1);
        },
      });
    }
    if (filters.city) {
      chips.push({
        key: 'city',
        label: `City: ${filters.city}`,
        onDelete: () => {
          setFilters((prev) => ({ ...prev, city: undefined, offset: 0 }));
          setPage(1);
        },
      });
    }
    return chips;
  }, [filters]);

  const queryParams: ListAttractionsParams = useMemo(
    () => ({
      ...filters,
      limit: pageSize,
      offset: (page - 1) * pageSize,
      ...(activeOnly && { activeOnly: true }),
      ...(searchDebounced && { search: searchDebounced }),
    }),
    [page, pageSize, filters, activeOnly, searchDebounced],
  );

  const { data, isLoading, isFetching, error, refetch } = useListAttractions(queryParams, {
    enabled: subtab === 'curation',
  });

  const attractions: Attraction[] = data?.result ?? [];
  const totalCount = data?.total ?? 0;
  const pageCount = Math.ceil(totalCount / pageSize);

  const handleClearFilters = useCallback(() => {
    setFilters({ sort: 'popularity', limit: DEFAULT_PAGE_SIZE, offset: 0 });
    setPage(1);
    setPageSize(DEFAULT_PAGE_SIZE);
    setActiveOnly(false);
    setSearchText('');
    setSearchDebounced('');
    setDrillCountryCodeOverride(null);
  }, []);

  const handleCountryFilterChange = useCallback(
    (_: unknown, value: { country: string; countryCode: string } | null) => {
      setFilters((prev) => ({
        ...prev,
        country: value?.country || undefined,
        state: undefined,
        province: undefined,
        city: undefined,
        offset: 0,
      }));
      setPage(1);
    },
    [],
  );

  const handleCityClick = useCallback((countryName: string, cityName: string) => {
    setFilters((prev) => ({
      ...prev,
      country: countryName,
      state: undefined,
      province: undefined,
      city: cityName,
      offset: 0,
    }));
    setPage(1);
  }, []);

  const handlePageChange = useCallback((_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  }, []);

  const handlePageSizeChange = useCallback((event: SelectChangeEvent<number>) => {
    const newSize = Number(event.target.value);
    setPageSize(newSize);
    setPage(1);
  }, []);

  const handleToggleActive = useCallback(
    (attractionId: string, currentActive: boolean) => {
      activeMutation.mutate({ attractionId, active: !currentActive });
    },
    [activeMutation.mutate],
  );

  const activeScope = useMemo(
    () => detectActiveScope({ ...filters, countryCode: selectedCountryCode }),
    [filters, selectedCountryCode],
  );
  const rankLimit = activeScope ? RANK_LIMIT : 0;

  const rankedQueryParams: ListAttractionsParams = useMemo(
    () => ({
      country: filters.country,
      countryCode: filters.countryCode,
      state: filters.state,
      province: filters.province,
      city: filters.city,
      sort: 'popularity',
      limit: 10,
      offset: 0,
      activeOnly: true,
    }),
    [filters.country, filters.countryCode, filters.state, filters.province, filters.city],
  );
  const { data: rankedData, isFetching: isRankedFetching } = useListAttractions(rankedQueryParams, {
    enabled: !!activeScope && subtab === 'curation',
  });
  const rankedAttractions: Attraction[] = rankedData?.result ?? [];

  const attractionListsFingerprint = useMemo(
    () => attractionRowSnapshotFingerprint(attractions, rankedAttractions),
    [attractions, rankedAttractions],
  );

  const rankingGeoKey = useMemo(
    () =>
      [
        filters.country,
        filters.countryCode ?? '',
        filters.state ?? '',
        filters.province ?? '',
        filters.city ?? '',
      ].join('|'),
    [filters.country, filters.countryCode, filters.state, filters.province, filters.city],
  );
  const prevRankingGeoKeyRef = useRef<string | null>(null);
  const attractionListsFingerprintRef = useRef('');

  useEffect(() => {
    if (prevRankingGeoKeyRef.current !== rankingGeoKey) {
      prevRankingGeoKeyRef.current = rankingGeoKey;
      setPendingRanks(new Map());
      const m = new Map<string, Attraction>();
      for (const a of attractions) {
        m.set(a.attractionId, a);
      }
      for (const a of rankedAttractions) {
        m.set(a.attractionId, a);
      }
      attractionListsFingerprintRef.current = attractionListsFingerprint;
      setAttractionRowSnapshotById(m);
      return;
    }
    if (attractionListsFingerprint === attractionListsFingerprintRef.current) {
      return;
    }
    attractionListsFingerprintRef.current = attractionListsFingerprint;
    setAttractionRowSnapshotById((prev) => {
      const next = new Map(prev);
      for (const a of attractions) {
        next.set(a.attractionId, a);
      }
      for (const a of rankedAttractions) {
        next.set(a.attractionId, a);
      }
      return next;
    });
  }, [rankingGeoKey, attractionListsFingerprint, attractions, rankedAttractions]);

  const usedRanks = useMemo(() => {
    if (!activeScope) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const a of rankedAttractions) {
      if (a.manualRank != null && a.manualRank >= 1 && a.manualRank <= rankLimit) {
        map.set(a.manualRank, a.attractionId);
      }
    }
    for (const [attractionId, rank] of pendingRanks.entries()) {
      if (rank == null) {
        for (const [r, aid] of map.entries()) {
          if (aid === attractionId) {
            map.delete(r);
            break;
          }
        }
      } else if (rank >= 1 && rank <= rankLimit) {
        for (const [r, aid] of map.entries()) {
          if (aid === attractionId) {
            map.delete(r);
            break;
          }
        }
        map.set(rank, attractionId);
      }
    }
    return map;
  }, [activeScope, rankedAttractions, pendingRanks, rankLimit]);

  const usedRanksRef = useRef(usedRanks);
  usedRanksRef.current = usedRanks;

  const handleRankChange = useCallback((attractionId: string, value: string) => {
    setPendingRanks((prev) => {
      const ranks = usedRanksRef.current;
      const next = new Map(prev);
      if (value === '') {
        next.set(attractionId, null);
      } else {
        const rank = Number(value);
        const currentHolder = ranks.get(rank);

        if (currentHolder && currentHolder !== attractionId) {
          let myCurrentRank: number | null = null;
          for (const [r, aid] of ranks.entries()) {
            if (aid === attractionId) {
              myCurrentRank = r;
              break;
            }
          }
          next.set(currentHolder, myCurrentRank);
        }

        next.set(attractionId, rank);
      }
      return next;
    });
  }, []);

  const handleSaveRankings = useCallback(() => {
    if (!activeScope || pendingRanks.size === 0) return;
    const rankings: RankChange[] = Array.from(pendingRanks.entries()).map(([attractionId, rank]) => ({
      attractionId,
      rank,
    }));
    rankMutation.mutate(
      { scopeType: activeScope.scopeType, scopeValue: activeScope.scopeValue, rankings },
      {
        onSuccess: () => {
          setPendingRanks(new Map());
          setAttractionRowSnapshotById(new Map());
          setPage(1);
        },
      },
    );
  }, [activeScope, pendingRanks, rankMutation.mutate]);

  const handleCountryClick = useCallback((countryName: string) => {
    setFilters((prev) => ({
      ...prev,
      country: countryName,
      state: undefined,
      province: undefined,
      city: undefined,
      offset: 0,
    }));
    setPage(1);
  }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const activeDragAttraction = useMemo(() => {
    if (!activeDragId) return null;
    const id = activeDragId.replace(/^main-/, '');
    return (
      attractions.find((a) => a.attractionId === id) ??
      rankedAttractions.find((a) => a.attractionId === id) ??
      attractionRowSnapshotById.get(id) ??
      null
    );
  }, [activeDragId, attractions, rankedAttractions, attractionRowSnapshotById]);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveDragId(String(event.active.id));
  }, []);

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;

    const rawActiveId = String(active.id);
    const activeId = rawActiveId.replace(/^main-/, '');
    const overId = String(over.id);

    const slotMatch = overId.match(/^slot-(\d+)$/);
    if (!slotMatch) return;

    const targetRank = Number(slotMatch[1]);
    setPendingRanks((prev) => computeSlotDrop(activeId, targetRank, usedRanksRef.current, prev));
  }, []);

  return (
    <Box>
      <Tabs value={subtab} onChange={handleSubtabChange} sx={{ mb: 2 }}>
        <Tab value="curation" label="Curation" />
        <Tab value="performance" label="Performance" />
      </Tabs>

      {subtab === 'performance' && <AttractionPerformancePage />}

      {subtab === 'curation' && (
        <>
          {isDashboardLoading && !dashboardStats && (
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1, mb: 3 }} />
          )}
          {isDashboardError && !isDashboardLoading && !dashboardStats && (
            <Paper variant="outlined" sx={{ mb: 3, py: 4, textAlign: 'center' }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 36, color: 'warning.main', mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Unable to load dashboard
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 340, mx: 'auto' }}>
                The analytics dashboard is temporarily unavailable. Try again or check your connection.
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<RefreshRoundedIcon />}
                onClick={() => refetchDashboard()}
              >
                Retry
              </Button>
            </Paper>
          )}
          {dashboardStats && (
            <AttractionsDashboard
              stats={dashboardStats}
              countries={countries}
              drillCountryCode={drillCountryCode}
              onDrillCountryChange={setDrillCountryCode}
              onCountryClick={handleCountryClick}
              onCityClick={handleCityClick}
            />
          )}

          {activeFilterChips.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap">
              {activeFilterChips.map((chip) => (
                <Chip key={chip.key} label={chip.label} size="small" onDelete={chip.onDelete} />
              ))}
            </Stack>
          )}

          <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
            <Stack spacing={2}>
              <TextField
                label="Search name"
                value={searchText}
                onChange={(e) => handleSearchChange(e.target.value)}
                size="small"
                fullWidth
                InputProps={{
                  ...(searchText && {
                    endAdornment: (
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSearchText('');
                          setSearchDebounced('');
                          setPage(1);
                        }}
                      >
                        <SearchOffRoundedIcon fontSize="small" />
                      </IconButton>
                    ),
                  }),
                }}
              />
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Autocomplete
                  options={countries}
                  getOptionLabel={(option) => option.country}
                  value={countries.find((c) => c.country === filters.country) ?? null}
                  onChange={handleCountryFilterChange}
                  renderInput={(params) => <TextField {...params} label="Country" />}
                  sx={{ minWidth: 170, flex: 1 }}
                  size="small"
                />
                <Autocomplete
                  options={filterOptions?.states ?? []}
                  value={filters.state ?? null}
                  onChange={(_, value) => {
                    setFilters((prev) => ({
                      ...prev,
                      state: value || undefined,
                      province: undefined,
                      city: undefined,
                      offset: 0,
                    }));
                    setPage(1);
                  }}
                  renderInput={(params) => <TextField {...params} label="State" />}
                  sx={{ minWidth: 150, flex: 1 }}
                  size="small"
                  disabled={!filters.country}
                />
                <Autocomplete
                  options={filterOptions?.provinces ?? []}
                  value={filters.province ?? null}
                  onChange={(_, value) => {
                    setFilters((prev) => ({ ...prev, province: value || undefined, city: undefined, offset: 0 }));
                    setPage(1);
                  }}
                  renderInput={(params) => <TextField {...params} label="Province" />}
                  sx={{ minWidth: 150, flex: 1 }}
                  size="small"
                  disabled={!filters.country}
                />
                <Autocomplete
                  options={filterOptions?.cities ?? filteredCities.map((c) => c.city)}
                  value={filters.city ?? null}
                  onChange={(_, value) => {
                    setFilters((prev) => ({ ...prev, city: value || undefined, offset: 0 }));
                    setPage(1);
                  }}
                  renderInput={(params) => <TextField {...params} label="City" />}
                  sx={{ minWidth: 150, flex: 1 }}
                  size="small"
                />
                <TextField
                  select
                  label="Sort"
                  value={filters.sort ?? 'popularity'}
                  onChange={(event) => {
                    setFilters((prev) => ({ ...prev, sort: event.target.value || undefined, offset: 0 }));
                    setPage(1);
                  }}
                  sx={{ minWidth: 120 }}
                  size="small"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={activeOnly}
                        size="small"
                        onChange={(_, checked) => {
                          setActiveOnly(checked);
                          setPage(1);
                        }}
                      />
                    }
                    label="Active only"
                    slotProps={{
                      typography: { variant: 'body2', color: 'text.secondary', sx: { whiteSpace: 'nowrap' } },
                    }}
                  />
                </Stack>
                {hasActiveFilters && (
                  <Button variant="text" size="small" onClick={handleClearFilters}>
                    Clear
                  </Button>
                )}
              </Stack>
            </Stack>
          </Paper>

          {error && !isLoading && (
            <Paper variant="outlined" sx={{ mb: 3, py: 4, textAlign: 'center' }}>
              <WarningAmberRoundedIcon sx={{ fontSize: 40, color: 'error.light', mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Unable to load attractions
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 360, mx: 'auto' }}>
                Something went wrong while fetching the data. Check your connection or try again.
              </Typography>
              <Button variant="outlined" size="small" startIcon={<RefreshRoundedIcon />} onClick={() => refetch()}>
                Retry
              </Button>
            </Paper>
          )}

          {!isLoading && !error && attractions.length === 0 && viewMode === 'flat' && (
            <Paper variant="outlined" sx={{ mb: 3, py: 4, textAlign: 'center' }}>
              <SearchOffRoundedIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                No attractions found
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 360, mx: 'auto' }}>
                Try adjusting your filters or search criteria to find what you are looking for.
              </Typography>
              <Button variant="outlined" size="small" onClick={handleClearFilters}>
                Clear filters
              </Button>
            </Paper>
          )}

          {!error && (isLoading || attractions.length > 0) && (
            <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                View:
              </Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, value) => {
                  if (value) setViewMode(value);
                }}
                size="small"
              >
                <ToggleButton value="flat" sx={{ px: 1.5, gap: 0.5 }}>
                  <FlatViewIcon fontSize="small" />
                  <Typography variant="caption" sx={{ textTransform: 'none' }}>
                    List
                  </Typography>
                </ToggleButton>
                <ToggleButton value="grouped" sx={{ px: 1.5, gap: 0.5 }}>
                  <GroupedViewIcon fontSize="small" />
                  <Typography variant="caption" sx={{ textTransform: 'none' }}>
                    Grouped
                  </Typography>
                </ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {activeScope && (
              <RankedAttractionsSummary
                key={`${activeScope.scopeType}:${activeScope.scopeValue}`}
                activeScope={activeScope}
                rankLimit={rankLimit}
                rankedAttractions={rankedAttractions}
                pageAttractions={attractions}
                attractionSnapshotById={attractionRowSnapshotById}
                pendingRanks={pendingRanks}
                isFetching={isRankedFetching}
              />
            )}

            <Paper
              variant="outlined"
              sx={{
                mt: 1,
                mb: 1,
                px: 2,
                py: 1.5,
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1,
                backgroundColor: alpha(theme.palette.info.main, 0.04),
                borderColor: alpha(theme.palette.info.main, 0.2),
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 18, color: 'info.main', mt: 0.2 }} />
              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.6 }}>
                <strong>Ranking:</strong> Attractions are ranked per geographic level. <strong>Popularity Rank</strong>{' '}
                (
                <Chip label="AI" size="small" color="primary" sx={{ height: 16, fontSize: '0.625rem', mx: 0.3 }} />) is
                computed by AI based on touristic relevance, factoring in cultural significance, visitor appeal, and
                offer coverage. <strong>State/Province/City Rank</strong> reflect position within that region. Rank #1
                means the attraction appears first in search results for that area.
              </Box>
            </Paper>

            {viewMode === 'flat' && !error && (isLoading || attractions.length > 0) && (
              <Paper variant="outlined" sx={{ mt: 1, width: '100%', position: 'relative' }}>
                {isFetching && (
                  <LinearProgress sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1, height: 2 }} />
                )}
                {activeScope && pendingRanks.size > 0 && (
                  <Box
                    sx={{
                      pl: 2,
                      pr: 1,
                      py: 1,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {pendingRanks.size} unsaved ranking change{pendingRanks.size > 1 ? 's' : ''}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => setPendingRanks(new Map())}
                      disabled={rankMutation.isPending}
                    >
                      Discard
                    </Button>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveRankings}
                      disabled={rankMutation.isPending}
                    >
                      {rankMutation.isPending ? 'Saving...' : 'Save Rankings'}
                    </Button>
                  </Box>
                )}
                <TableContainer sx={{ transition: 'opacity 0.2s ease' }}>
                  <Table
                    size="small"
                    sx={{ '& .MuiTableCell-body': { fontSize: '0.8125rem', verticalAlign: 'middle' } }}
                  >
                    <TableHead>
                      <TableRow>
                        {TABLE_COLUMNS.map((col, idx) => (
                          <TableCell
                            key={col || `__col_${idx}`}
                            sx={{
                              padding: 1,
                              fontSize: 12,
                              fontWeight: 600,
                              color: 'white',
                              whiteSpace: 'nowrap',
                              backgroundColor: theme.palette.primary.dark,
                            }}
                          >
                            {col}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {isLoading && attractions.length === 0
                        ? Array.from({ length: 8 }).map((_, i) => (
                            <TableRow key={i}>
                              {TABLE_COLUMNS.map((col, idx) => (
                                <TableCell key={col || `__skel_${idx}_${i}`}>
                                  <Skeleton variant="rounded" height={20} sx={{ minWidth: 40 }} />
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        : attractions.map((row) => (
                            <TableRow
                              key={row.id}
                              hover
                              sx={{
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(themeColor, 0.08) },
                                '&:last-child td': { borderBottom: 0 },
                              }}
                              onClick={() => history.push(`/attractions/${row.attractionId}`)}
                            >
                              <TableCell sx={{ padding: 0.5, width: 32 }} onClick={(e) => e.stopPropagation()}>
                                {activeScope && <DraggableHandle attractionId={row.attractionId} />}
                              </TableCell>

                              <TableCell sx={{ padding: 1, width: 48 }}>
                                <Tooltip title="View details">
                                  <IconButton
                                    size="small"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      history.push(`/attractions/${row.attractionId}`);
                                    }}
                                  >
                                    <ViewIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>

                              <TableCell sx={{ padding: 1, width: 72 }}>
                                <ImageWithFallback
                                  src={
                                    row.imageUrl?.startsWith('http')
                                      ? row.imageUrl
                                      : row.imageUrl
                                      ? `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_100,h_100/${row.imageUrl}`
                                      : undefined
                                  }
                                />
                              </TableCell>

                              <TableCell sx={{ padding: 1 }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: '0.8125rem',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {row.name}
                                </Typography>
                                <Typography
                                  sx={{
                                    fontFamily: 'monospace',
                                    color: 'text.disabled',
                                    fontSize: '0.6875rem',
                                  }}
                                >
                                  {row.attractionId}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ padding: 1 }}>
                                <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                  {[row.countryCode, row.state, row.province, row.city].filter(Boolean).join(' / ')}
                                </Typography>
                              </TableCell>

                              <TableCell sx={{ padding: 1 }}>
                                <Chip
                                  label={formatAttractionType(row.type)}
                                  size="small"
                                  sx={{
                                    bgcolor: TYPE_COLORS[row.type]?.bg ?? 'grey.500',
                                    color: TYPE_COLORS[row.type]?.text ?? '#fff',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                  }}
                                />
                              </TableCell>

                              <TableCell sx={{ padding: 1, textAlign: 'center' }}>{row.offerCount}</TableCell>

                              <TableCell
                                sx={{ padding: 1, textAlign: 'center' }}
                                onClick={(e) => activeScope?.scopeType === 'country' && e.stopPropagation()}
                              >
                                <RankCell
                                  row={row}
                                  scopeType="country"
                                  activeScope={activeScope}
                                  pendingRanks={pendingRanks}
                                  rankLimit={rankLimit}
                                  usedRanks={usedRanks}
                                  onRankChange={handleRankChange}
                                  fallback={
                                    <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
                                      <Typography variant="body2">#{row.popularityRank}</Typography>
                                      {row.active && row.popularityRank <= 30 && (
                                        <Tooltip title="AI-ranked by touristic relevance">
                                          <Chip
                                            label="AI"
                                            size="small"
                                            sx={{
                                              height: 18,
                                              fontSize: '0.625rem',
                                              fontWeight: 700,
                                              bgcolor: '#7C3AED',
                                              color: '#fff',
                                            }}
                                          />
                                        </Tooltip>
                                      )}
                                    </Stack>
                                  }
                                />
                              </TableCell>

                              <TableCell
                                sx={{ padding: 1, textAlign: 'center' }}
                                onClick={(e) => activeScope?.scopeType === 'state' && e.stopPropagation()}
                              >
                                <RankCell
                                  row={row}
                                  scopeType="state"
                                  activeScope={activeScope}
                                  pendingRanks={pendingRanks}
                                  rankLimit={rankLimit}
                                  usedRanks={usedRanks}
                                  onRankChange={handleRankChange}
                                  fallback={
                                    <Typography variant="body2">{row.stateRank ? `#${row.stateRank}` : '-'}</Typography>
                                  }
                                />
                              </TableCell>

                              <TableCell
                                sx={{ padding: 1, textAlign: 'center' }}
                                onClick={(e) => activeScope?.scopeType === 'province' && e.stopPropagation()}
                              >
                                <RankCell
                                  row={row}
                                  scopeType="province"
                                  activeScope={activeScope}
                                  pendingRanks={pendingRanks}
                                  rankLimit={rankLimit}
                                  usedRanks={usedRanks}
                                  onRankChange={handleRankChange}
                                  fallback={
                                    <Typography variant="body2">
                                      {row.provinceRank ? `#${row.provinceRank}` : '-'}
                                    </Typography>
                                  }
                                />
                              </TableCell>

                              <TableCell
                                sx={{ padding: 1, textAlign: 'center' }}
                                onClick={(e) => activeScope?.scopeType === 'city' && e.stopPropagation()}
                              >
                                <RankCell
                                  row={row}
                                  scopeType="city"
                                  activeScope={activeScope}
                                  pendingRanks={pendingRanks}
                                  rankLimit={rankLimit}
                                  usedRanks={usedRanks}
                                  onRankChange={handleRankChange}
                                  fallback={
                                    <Typography variant="body2">{row.cityRank ? `#${row.cityRank}` : '-'}</Typography>
                                  }
                                />
                              </TableCell>

                              <TableCell sx={{ padding: 1 }}>
                                <Switch
                                  checked={row.active}
                                  size="small"
                                  onChange={(e) => {
                                    e.stopPropagation();
                                    handleToggleActive(row.attractionId, row.active);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 1.5, py: 0.75, borderTop: '1px solid', borderColor: 'divider' }}
                >
                  <Pagination count={pageCount} page={page} onChange={handlePageChange} color="primary" size="small" />
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Typography variant="caption" color="text.disabled">
                      {totalCount} attractions
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        Per page:
                      </Typography>
                      <Select
                        size="small"
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        sx={{ fontSize: '0.75rem', height: 28, minWidth: 60 }}
                      >
                        {[10, 25, 50].map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        ))}
                      </Select>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            )}

            <DragOverlay dropAnimation={null}>
              {activeDragAttraction && (
                <Paper
                  elevation={8}
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1, maxWidth: 400 }}
                >
                  <ImageWithFallback
                    src={
                      activeDragAttraction.imageUrl?.startsWith('http')
                        ? activeDragAttraction.imageUrl
                        : activeDragAttraction.imageUrl
                        ? `${window.configs.IMAGE_HOST}/fl_progressive,q_auto:eco,c_fill,g_auto,w_60,h_60/${activeDragAttraction.imageUrl}`
                        : undefined
                    }
                    size="small"
                  />
                  <Typography sx={{ fontWeight: 600, fontSize: '0.8125rem' }}>{activeDragAttraction.name}</Typography>
                </Paper>
              )}
            </DragOverlay>
          </DndContext>

          {viewMode === 'grouped' && !error && (
            <Box>
              {isGroupedLoading && <LinearProgress sx={{ mb: 1, borderRadius: 1 }} />}
              {[...(groupedStats?.countries ?? [])]
                .sort((a, b) => b.attractionCount - a.attractionCount)
                .map((country) => {
                  const color = coverageColor(country.coverageRate);
                  const paletteColor = theme.palette[color].main;

                  return (
                    <Accordion
                      key={country.countryCode}
                      disableGutters
                      elevation={0}
                      sx={{
                        mb: 1,
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: '8px !important',
                        overflow: 'hidden',
                        '&:before': { display: 'none' },
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          bgcolor: alpha(paletteColor, 0.04),
                          borderBottom: '1px solid',
                          borderColor: alpha(paletteColor, 0.1),
                          minHeight: 56,
                          '&.Mui-expanded': { minHeight: 56 },
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                          <Typography variant="subtitle1" fontWeight="bold" sx={{ minWidth: 180 }}>
                            {country.country}
                          </Typography>
                          <Chip
                            label={`${country.coverageRate}%`}
                            size="small"
                            color={color}
                            sx={{ fontWeight: 'bold', minWidth: 56 }}
                          />
                          <Box sx={{ flex: 1, maxWidth: 120 }}>
                            <LinearProgress
                              variant="determinate"
                              value={country.coverageRate}
                              color={color}
                              sx={{ height: 4, borderRadius: 2 }}
                            />
                          </Box>
                          <Stack direction="row" spacing={2} sx={{ ml: 'auto' }}>
                            <Typography variant="caption" color="text.secondary">
                              {pluralize(country.attractionCount, 'attraction', 'attractions')}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {country.coveredCount} covered
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {country.totalOffers.toLocaleString()} offers
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {pluralize(country.cityCount, 'city', 'cities')}
                            </Typography>
                          </Stack>
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails sx={{ p: 0 }}>
                        {[...(country.cities ?? [])]
                          .sort((a, b) => b.attractionCount - a.attractionCount)
                          .map((city, idx) => {
                            const cityColor = coverageColor(city.coverageRate);
                            const cityPalette = theme.palette[cityColor].main;
                            const isEven = idx % 2 === 0;

                            return (
                              <Stack
                                key={`${country.countryCode}-${city.city}`}
                                direction="row"
                                spacing={2}
                                alignItems="center"
                                sx={{
                                  px: 3,
                                  py: 1.25,
                                  bgcolor: isEven ? 'transparent' : alpha(themeColor, 0.015),
                                  borderBottom: '1px solid',
                                  borderColor: alpha(themeColor, 0.06),
                                  cursor: 'pointer',
                                  transition: 'all 0.1s ease',
                                  '&:hover': {
                                    bgcolor: alpha(themeColor, 0.06),
                                  },
                                  '&:last-child': { borderBottom: 'none' },
                                }}
                                onClick={() => {
                                  setFilters((prev) => ({
                                    ...prev,
                                    country: country.country,
                                    city: city.city,
                                    offset: 0,
                                  }));
                                  setPage(1);
                                  setViewMode('flat');
                                }}
                              >
                                <Typography variant="body2" fontWeight="medium" sx={{ minWidth: 200 }}>
                                  {city.city}
                                </Typography>
                                <Box sx={{ flex: 1, maxWidth: 180 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={city.coverageRate}
                                    color={cityColor}
                                    sx={{ height: 8, borderRadius: 4, bgcolor: alpha(cityPalette, 0.1) }}
                                  />
                                </Box>
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  color={`${cityColor}.main`}
                                  sx={{ minWidth: 50, textAlign: 'right' }}
                                >
                                  {city.coverageRate}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 70 }}>
                                  {city.attractionCount} attr.
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                  {city.coveredCount} covered
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ minWidth: 80 }}>
                                  {city.totalOffers} offers
                                </Typography>
                              </Stack>
                            );
                          })}
                      </AccordionDetails>
                    </Accordion>
                  );
                })}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
